import { Location } from 'history';
import { Children, useEffect } from 'react';
import { match, useLocation } from 'react-router-dom';

import { useFlags } from '@project44/feature-flags';
import { useTranslation } from '@project44/translations-utils';
import { getP44Env, useUser } from '@project44/ui-utils';

import movementLogo from '../../assets/images/movementLogo.svg';
import { navigationLinksForUser } from '../../utils/constants/NAVIGATION_LINKS';
import Icon from '../Icon';
import IconLibrary from '../Icon/IconLibrary';
import Menu from './components/Menu';

import {
  AvatarContainer,
  Container,
  Divider,
  LinkButton,
  Logo,
} from './styles';

const additionalRoutes: Record<string, string[]> = {
  '/results': ['^/workspace(/.*)*$'],
};

export const SideBar = (props: {
  toggleTenantSwitcherComponent: () => void;
}) => {
  const { data: user } = useUser({ skipRedirectOn401: true });
  const location = useLocation();
  const { t } = useTranslation();
  const flags = useFlags();

  // redirect to the environment appropriate movement url if on the old na14/eu14 url
  useEffect(() => {
    const { MOVEMENT_BASE_URL } = getP44Env();
    if (window.location.origin !== MOVEMENT_BASE_URL) {
      window.location.replace(
        `${MOVEMENT_BASE_URL}${location.pathname}${location.search}`,
      );
    }
  }, []);

  const shouldRenderAvatar = !!user;

  const isActive = (url: string, match: match | null, location: Location) => {
    const isResultsSubPath = location.pathname.includes('/results/');

    if (isResultsSubPath) {
      return location.pathname.includes('/dashboard')
        ? url.includes('/dashboard')
        : location.pathname.includes(url);
    }

    if (match) return true;

    if (additionalRoutes[url]) {
      return additionalRoutes[url].some((pattern) =>
        location.pathname.match(pattern),
      );
    }

    return false;
  };

  return (
    <>
      <IconLibrary />
      <Container>
        <div
          data-testid={
            user
              ? 'side-nav-container-links-visible'
              : 'side-nav-container-links-hidden'
          }
        >
          <Logo src={movementLogo} alt={t('movementLogo')} />
          <Divider />
          <div>
            {user &&
              Children.toArray(
                navigationLinksForUser({ user, flags }).map(
                  ({ url, iconName }, i) => (
                    <LinkButton
                      isActive={(match, location) =>
                        isActive(url, match, location)
                      }
                      to={url}
                      activeClassName="is-active"
                      data-testid={`link-${iconName}`}
                      key={i}
                    >
                      <Icon name={iconName} />
                    </LinkButton>
                  ),
                ),
              )}
          </div>
        </div>
        {shouldRenderAvatar && (
          <AvatarContainer>
            <Menu
              toggleTenantSwitcherComponent={
                props.toggleTenantSwitcherComponent
              }
            />
          </AvatarContainer>
        )}
      </Container>
    </>
  );
};

export const SideBarSkeleton = () => {
  const { t } = useTranslation();
  return (
    <>
      <IconLibrary />
      <Container>
        <div data-testid="side-nav-container-skeleton">
          <Logo src={movementLogo} alt={t('movementLogo')} />
          <Divider />
        </div>
      </Container>
    </>
  );
};
